import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  Agriculture as AgricultureIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';
// Importer getPendingFarmers et getPendingAdminRequests
import { getPendingFarmers, getPendingAdminRequests } from '../../services/admin/adminService';
import { approveFarmer, rejectFarmer } from '../../services/farmer/adminApproval';
import { sendApprovalNotification, sendRejectionNotification } from '../../services/farmer/notifications';
import { useAuth } from '../AuthContext';
import { getAuth } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { doc, getDoc, deleteDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';

/**
 * Composant pour gérer les agriculteurs et administrateurs en attente d'approbation
 */
const PendingFarmers = () => {
  const [pendingFarmers, setPendingFarmers] = useState([]);
  const [pendingAdmins, setPendingAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectAdminDialogOpen, setRejectAdminDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isPDG, setIsPDG] = useState(false);
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Vérifier si l'utilisateur actuel est PDG
  useEffect(() => {
    const checkUserRole = async () => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists() && userDoc.data().role === 'PDG') {
            setIsPDG(true);
          }
        } catch (err) {
          console.error('Erreur lors de la vérification du rôle:', err);
        }
      }
    };
    
    checkUserRole();
  }, [currentUser]);

  // Charger les agriculteurs et administrateurs en attente d'approbation
  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const [farmers, admins] = await Promise.all([
        getPendingFarmers(),
        getPendingAdminRequests()
      ]);
      setPendingFarmers(farmers);
      setPendingAdmins(admins);
    } catch (err) {
      console.error('Erreur lors du chargement des demandes en attente:', err);
      setError('Impossible de charger les demandes en attente. ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (farmer) => {
    try {
      setLoading(true);
      const result = await approveFarmer(farmer.id, currentUser.uid);
      
      if (result.success) {
        // Envoyer une notification à l'agriculteur
        await sendApprovalNotification(farmer.id);
        
        setPendingFarmers(prevFarmers => 
          prevFarmers.filter(f => f.id !== farmer.id)
        );
        setSuccessMessage(`L'agriculteur ${farmer.name || farmer.fullName} a été approuvé avec succès et notifié par email.`);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Erreur lors de l\'approbation de l\'agriculteur:', err);
      setError('Une erreur est survenue lors de l\'approbation.');
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  };

  const openRejectDialog = (farmer) => {
    setSelectedFarmer(farmer);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedFarmer) return;
    
    try {
      setLoading(true);
      setRejectDialogOpen(false);
      
      const result = await rejectFarmer(selectedFarmer.id, currentUser.uid, rejectionReason);
      
      if (result.success) {
        // Envoyer une notification à l'agriculteur
        await sendRejectionNotification(selectedFarmer.id, rejectionReason);
        
        setPendingFarmers(prevFarmers => 
          prevFarmers.filter(f => f.id !== selectedFarmer.id)
        );
        setSuccessMessage(`La demande de l'agriculteur ${selectedFarmer.name || selectedFarmer.fullName} a été rejetée et l'utilisateur a été notifié par email.`);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Erreur lors du rejet de l\'agriculteur:', err);
      setError('Une erreur est survenue lors du rejet.');
    } finally {
      setLoading(false);
      setSelectedFarmer(null);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  };

  // Fonction pour rendre les informations de l'agriculteur
  const renderFarmerInfo = (farmer) => {
    return (
      <>
        <Typography component="span" variant="body2">
          Email: {farmer.email}
        </Typography>
        <br />
        <Typography component="span" variant="body2">
          Téléphone: {farmer.phoneNumber || farmer.phone || 'Non spécifié'}
        </Typography>
        <br />
        <Typography component="span" variant="body2">
          Ferme: {farmer.farmName} ({farmer.farmLocation || farmer.location})
        </Typography>
        {farmer.province && (
          <>
            <br />
            <Typography component="span" variant="body2">
              Province: {farmer.province.charAt(0).toUpperCase() + farmer.province.slice(1).replace(/-/g, ' ')}
            </Typography>
          </>
        )}
        {farmer.farmType && (
          <>
            <br />
            <Typography component="span" variant="body2">
              Type d'exploitation: {
                farmer.farmType === "maraichage" ? "Maraîchage" :
                farmer.farmType === "cereales" ? "Céréales" :
                farmer.farmType === "elevage" ? "Élevage" :
                farmer.farmType === "arboriculture" ? "Arboriculture" :
                farmer.farmType === "viticulture" ? "Viticulture" :
                "Autre"
              }
            </Typography>
          </>
        )}
        {farmer.crops && (
          <>
            <br />
            <Typography component="span" variant="body2">
              Cultures: {farmer.crops}
            </Typography>
          </>
        )}
        {farmer.hasPlot && (
          <>
            <br />
            <Typography component="span" variant="body2">
              Terrain: {farmer.plotSize} hectares
            </Typography>
          </>
        )}
        {farmer.hasExpertise && farmer.expertise && (
          <>
            <br />
            <Typography component="span" variant="body2">
              Expertise: {farmer.expertise}
            </Typography>
          </>
        )}
        {farmer.isCooperativeMember && (
          <>
            <br />
            <Typography component="span" variant="body2">
              Coopérative: {farmer.cooperativeName} ({farmer.cooperativeMembers} membres)
            </Typography>
          </>
        )}
        <br />
        <Typography component="span" variant="body2" color="text.secondary">
          Inscription: {formatDate(farmer.createdAt)}
        </Typography>
      </>
    );
  };

  // État pour stocker le poste de l'administrateur
  const [adminPosition, setAdminPosition] = useState('');
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [selectedAdminForApproval, setSelectedAdminForApproval] = useState(null);

  // Fonctions pour gérer les demandes d'administrateurs
  const openPositionDialog = (admin) => {
    setSelectedAdminForApproval(admin);
    setAdminPosition('');
    setPositionDialogOpen(true);
  };

  const approveAdminRequest = async () => {
    if (!selectedAdminForApproval || !adminPosition.trim()) return;
    
    try {
      setLoading(true);
      setPositionDialogOpen(false);
      
      // Récupérer les informations de la demande
      const { email, name, password } = selectedAdminForApproval;
      
      // Vérifier si l'utilisateur a les permissions nécessaires
      if (!isPDG && !currentUser) {
        throw new Error('Vous n\'avez pas les permissions nécessaires pour approuver un administrateur.');
      }
      
      try {
        // Récupérer d'abord la demande originale pour s'assurer qu'elle existe
        const requestRef = doc(db, 'admin_requests', selectedAdminForApproval.id);
        const requestDoc = await getDoc(requestRef);
        
        if (!requestDoc.exists()) {
          throw new Error('La demande d\'administrateur n\'existe plus ou a déjà été traitée.');
        }
        
        // Importer le service de création d'administrateur sans déconnexion
        const { createAdminWithoutAuth } = await import('../../services/admin/adminFirebaseService');
        
        // Préparer les données de l'administrateur
        const adminData = {
          name,
          email,
          position: adminPosition.trim(),
          requestId: selectedAdminForApproval.id,
          password // Utiliser le mot de passe fourni lors de la demande
        };
        
        // Créer l'administrateur sans déconnecter l'utilisateur actuel
        const result = await createAdminWithoutAuth(adminData, currentUser ? currentUser.uid : null);
        
        if (result.success) {
          // Mettre à jour la liste des demandes
          setPendingAdmins(prevAdmins => 
            prevAdmins.filter(admin => admin.id !== selectedAdminForApproval.id)
          );
          
          setSuccessMessage(`${name} a été approuvé avec succès en tant qu'administrateur avec le poste: ${adminPosition}. Un compte d'authentification a été automatiquement créé.`);
        } else {
          throw new Error(result.error || 'Erreur lors de la création du compte administrateur.');
        }
      } catch (firestoreError) {
        console.error('Erreur Firestore:', firestoreError);
        throw new Error('Erreur lors de l\'approbation de la demande: ' + (firestoreError.message || 'Problème d\'accès à la base de données'));
      }
      
      setTimeout(() => setSuccessMessage(''), 8000);
    } catch (error) {
      console.error('Erreur lors de l\'approbation de la demande:', error);
      let errorMessage = 'Une erreur est survenue lors de l\'approbation de la demande.';
      
      // Gestion des erreurs spécifiques
      if (error.code === 'permission-denied' || error.message.includes('permission')) {
        errorMessage = 'Permissions insuffisantes. Veuillez contacter l\'administrateur système.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
      setSelectedAdminForApproval(null);
      setAdminPosition('');
    }
  };
  
  const openRejectAdminDialog = (admin) => {
    setSelectedAdmin(admin);
    setRejectionReason('');
    setRejectAdminDialogOpen(true);
  };
  
  const rejectAdminRequest = async () => {
    if (!selectedAdmin) return;
    
    try {
      setLoading(true);
      setRejectAdminDialogOpen(false);
      
      // Vérifier d'abord que la demande existe toujours
      const requestRef = doc(db, 'admin_requests', selectedAdmin.id);
      const requestDoc = await getDoc(requestRef);
      
      if (!requestDoc.exists()) {
        throw new Error('La demande d\'administrateur n\'existe plus ou a déjà été traitée.');
      }
      
      // Créer un batch pour effectuer l'opération de suppression
      const batch = writeBatch(db);
      
      // Ajouter l'opération de suppression au batch
      batch.delete(requestRef);
      
      // Créer une entrée dans la collection admin_rejections pour traçabilité
      const rejectionRef = doc(collection(db, 'admin_rejections'));
      const rejectionData = {
        adminEmail: selectedAdmin.email,
        adminName: selectedAdmin.name,
        rejectedBy: currentUser ? currentUser.uid : null,
        rejectedAt: new Date().toISOString(),
        reason: rejectionReason || 'Aucune raison spécifiée',
        originalRequestId: selectedAdmin.id
      };
      
      // Ajouter l'opération de création de l'entrée de rejet au batch
      batch.set(rejectionRef, rejectionData);
      
      // Exécuter toutes les opérations en une seule transaction
      await batch.commit();
      
      // Mettre à jour la liste des demandes
      setPendingAdmins(prevAdmins => 
        prevAdmins.filter(admin => admin.id !== selectedAdmin.id)
      );
      
      setSuccessMessage(`La demande de ${selectedAdmin.name} a été rejetée.`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Erreur lors du rejet de la demande:', error);
      let errorMessage = 'Une erreur est survenue lors du rejet de la demande.';
      
      // Gestion des erreurs spécifiques
      if (error.code === 'permission-denied' || error.message.includes('permission')) {
        errorMessage = 'Permissions insuffisantes. Veuillez contacter l\'administrateur système.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
      setSelectedAdmin(null);
    }
  };

  // Rendu des boutons d'action pour mobile
  const renderMobileActions = (farmer) => (
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button 
        variant="contained" 
        color="success" 
        size="small"
        onClick={() => handleApprove(farmer)}
        sx={{ mr: 1 }}
        disabled={loading}
        startIcon={<CheckIcon />}
      >
        Approuver
      </Button>
      <Button 
        variant="contained" 
        color="error" 
        size="small"
        onClick={() => openRejectDialog(farmer)}
        disabled={loading}
        startIcon={<CloseIcon />}
      >
        Rejeter
      </Button>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, color: '#014527', fontWeight: 'bold' }}>
        Demandes en attente d'approbation
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ 
          p: 3, 
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Demandes en attente
          </Typography>
          
          {pendingFarmers.length > 0 || pendingAdmins.length > 0 ? (
            <List>
              {/* Affichage des demandes d'agriculteurs */}
              {pendingFarmers.map((farmer, index) => (
                <React.Fragment key={`farmer-${farmer.id}`}>
                  {isMobile ? (
                    <Accordion sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' } }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ px: 1 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AgricultureIcon sx={{ color: '#f59e0b', mr: 1 }} />
                          <Typography>{farmer.name || farmer.fullName}</Typography>
                          <Chip 
                            label="Agriculteur" 
                            size="small" 
                            sx={{ ml: 1, bgcolor: '#f59e0b', color: 'white', fontSize: '0.7rem' }} 
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {renderFarmerInfo(farmer)}
                        {renderMobileActions(farmer)}
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <ListItem
                      secondaryAction={
                        <Box>
                          <Button 
                            variant="contained" 
                            color="success" 
                            size="small"
                            onClick={() => handleApprove(farmer)}
                            sx={{ mr: 1 }}
                            disabled={loading}
                            startIcon={<CheckIcon />}
                          >
                            Approuver
                          </Button>
                          <Button 
                            variant="contained" 
                            color="error" 
                            size="small"
                            onClick={() => openRejectDialog(farmer)}
                            disabled={loading}
                            startIcon={<CloseIcon />}
                          >
                            Rejeter
                          </Button>
                        </Box>
                      }
                    >
                      <ListItemIcon>
                        <AgricultureIcon sx={{ color: '#f59e0b' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography>{farmer.name || farmer.fullName}</Typography>
                            <Chip 
                              label="Agriculteur" 
                              size="small" 
                              sx={{ ml: 1, bgcolor: '#f59e0b', color: 'white', fontSize: '0.7rem' }} 
                            />
                          </Box>
                        }
                        secondary={renderFarmerInfo(farmer)}
                      />
                    </ListItem>
                  )}
                  {!isMobile && index < pendingFarmers.length - 1 && <Divider />}
                </React.Fragment>
              ))}

              {/* Affichage des demandes d'administrateurs */}
              {pendingAdmins.length > 0 && pendingFarmers.length > 0 && !isMobile && <Divider sx={{ my: 2 }} />}
              
              {pendingAdmins.map((admin, index) => (
                <React.Fragment key={`admin-${admin.id}`}>
                  {isMobile ? (
                    <Accordion sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' } }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ px: 1 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AdminIcon sx={{ color: '#2563eb', mr: 1 }} />
                          <Typography>{admin.name}</Typography>
                          <Chip 
                            label="Administrateur" 
                            size="small" 
                            sx={{ ml: 1, bgcolor: '#2563eb', color: 'white', fontSize: '0.7rem' }} 
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography component="span" variant="body2">
                          Email: {admin.email}
                        </Typography>
                        <br />
                        <Typography component="span" variant="body2" color="text.secondary">
                          Demande soumise le: {formatDate(admin.createdAt)}
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button 
                            variant="contained" 
                            color="success" 
                            size="small"
                            onClick={() => openPositionDialog(admin)}
                            sx={{ mr: 1 }}
                            disabled={!isPDG || loading}
                          >
                            Approuver
                          </Button>
                          <Button 
                            variant="contained" 
                            color="error" 
                            size="small"
                            onClick={() => openRejectAdminDialog(admin)}
                            disabled={!isPDG || loading}
                          >
                            Rejeter
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <ListItem
                      secondaryAction={
                        <Box>
                          <Button 
                            variant="contained" 
                            color="success" 
                            size="small"
                            onClick={() => openPositionDialog(admin)}
                            sx={{ mr: 1 }}
                            disabled={!isPDG || loading}
                          >
                            Approuver
                          </Button>
                          <Button 
                            variant="contained" 
                            color="error" 
                            size="small"
                            onClick={() => openRejectAdminDialog(admin)}
                            disabled={!isPDG || loading}
                          >
                            Rejeter
                          </Button>
                        </Box>
                      }
                    >
                      <ListItemIcon>
                        <AdminIcon sx={{ color: '#2563eb' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography>{admin.name}</Typography>
                            <Chip 
                              label="Administrateur" 
                              size="small" 
                              sx={{ ml: 1, bgcolor: '#2563eb', color: 'white', fontSize: '0.7rem' }} 
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography component="span" variant="body2">
                              Email: {admin.email}
                            </Typography>
                            <br />
                            <Typography component="span" variant="body2" color="text.secondary">
                              Demande soumise le: {formatDate(admin.createdAt)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  )}
                  {!isMobile && index < pendingAdmins.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
              Aucune demande en attente d'approbation.
            </Typography>
          )}
        </Paper>
      )}

      {/* Dialogue de confirmation pour le rejet d'agriculteur */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Rejeter la demande</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir rejeter la demande de {selectedFarmer?.name || selectedFarmer?.fullName} ?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="reason"
            label="Raison du rejet (optionnel)"
            type="text"
            fullWidth
            variant="outlined"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} color="primary">
            Annuler
          </Button>
          <Button onClick={handleReject} color="error" variant="contained">
            Rejeter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de confirmation pour le rejet d'administrateur */}
      <Dialog open={rejectAdminDialogOpen} onClose={() => setRejectAdminDialogOpen(false)}>
        <DialogTitle>Rejeter la demande d'administrateur</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir rejeter la demande d'administrateur de {selectedAdmin?.name} ?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="adminReason"
            label="Raison du rejet (optionnel)"
            type="text"
            fullWidth
            variant="outlined"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectAdminDialogOpen(false)} color="primary">
            Annuler
          </Button>
          <Button onClick={rejectAdminRequest} color="error" variant="contained">
            Rejeter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue pour saisir le poste de l'administrateur avant approbation */}
      <Dialog open={positionDialogOpen} onClose={() => setPositionDialogOpen(false)}>
        <DialogTitle>Définir le poste de l'administrateur</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Veuillez renseigner le poste de {selectedAdminForApproval?.name} avant de l'approuver en tant qu'administrateur.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="adminPosition"
            label="Poste de l'administrateur *"
            type="text"
            fullWidth
            variant="outlined"
            value={adminPosition}
            onChange={(e) => setAdminPosition(e.target.value)}
            required
            error={adminPosition.trim() === ''}
            helperText={adminPosition.trim() === '' ? 'Le poste est obligatoire' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPositionDialogOpen(false)} color="primary">
            Annuler
          </Button>
          <Button 
            onClick={approveAdminRequest} 
            color="success" 
            variant="contained"
            disabled={adminPosition.trim() === ''}
          >
            Approuver
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingFarmers;